import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './FanAccount.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://saskararecord.com/api';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

const STATUS_LABEL = { pending: 'Menunggu Pembayaran', paid: 'Lunas' };
const TIER_LABEL = { casual: 'Casual', superfan: 'Superfan', vip: 'VIP' };

export default function FanAccount() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', phone: '', address: '', photo_url: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [feed, setFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);

  const token = localStorage.getItem('saskara_user_token');

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/users-auth/me`, authHeader);
      setData(res.data);
      setForm({
        name: res.data.name || '',
        phone: res.data.phone || '',
        address: res.data.address || '',
        photo_url: res.data.photo_url || '',
      });
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeed = async () => {
    setFeedLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/users-auth/me/feed`, authHeader);
      setFeed(res.data.feed || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFeedLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await axios.post(`${API_BASE}/admin-upload/fan-photo`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm((f) => ({ ...f, photo_url: res.data.url }));
    } catch (err) {
      alert('Gagal upload foto');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/users-auth/me`, form, authHeader);
      alert('Profil berhasil disimpan');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal simpan profil');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('saskara_user_token');
    localStorage.removeItem('saskara_user_info');
    navigate('/');
  };

  if (loading || !data) {
    return <div className="fan-account"><p className="fan-account__loading">Memuat...</p></div>;
  }

  return (
    <div className="fan-account">
      <nav className="fan-account__nav">
        <Link to="/" className="fan-account__logo">SASKARA<span>RECORD</span></Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/" className="fan-account__back-link">← Beranda</Link>
          <button className="fan-account__logout" onClick={handleLogout}>Keluar</button>
        </div>
      </nav>

      <div className="fan-account__body">
        <p className="fan-account__greeting">Hai, {data.name} 👋</p>
        <h1 className="fan-account__title">Akun Saya</h1>

        <div className="fan-account__tabs">
          <button className={tab === 'profile' ? 'active' : ''} onClick={() => setTab('profile')}>Profil</button>
          <button className={tab === 'orders' ? 'active' : ''} onClick={() => setTab('orders')}>Riwayat Pesanan</button>
          <button className={tab === 'following' ? 'active' : ''} onClick={() => setTab('following')}>Mengikuti</button>
        </div>

        {tab === 'profile' && (
          <form className="fan-account__form" onSubmit={handleSave}>
            <div className="fan-account__photo-row">
              <div className="fan-account__photo-box">
                {form.photo_url && <img src={form.photo_url} alt={data.name} />}
              </div>
              <label className="fan-account__upload-btn">
                {uploading ? 'Mengupload...' : '📁 Ganti Foto'}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} disabled={uploading} style={{ display: 'none' }} />
              </label>
            </div>

            <label>Nama Lengkap</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />

            <label>Email</label>
            <input type="email" value={data.email} disabled style={{ opacity: 0.6 }} />

            <label>Nomor HP</label>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxxx" />

            <label>Alamat</label>
            <textarea rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Alamat lengkap untuk pengiriman merchandise" />

            <div className="fan-account__tier">Tier: <strong>{TIER_LABEL[data.tier] || data.tier}</strong></div>
            {data.referral_code && (
              <div className="fan-account__tier">Kode Referral kamu: <strong>{data.referral_code}</strong></div>
            )}

            <button type="submit" className="fan-account__save-btn" disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan Profil'}
            </button>
          </form>
        )}

        {tab === 'orders' && (
          <div className="fan-account__list">
            {data.orders.length === 0 ? (
              <p className="fan-account__empty">Belum ada pesanan.</p>
            ) : (
              data.orders.map((o) => (
                <div className="fan-account__order-card" key={o.id}>
                  <div>
                    <p className="fan-account__order-id">Order #{o.id}</p>
                    <p className="fan-account__order-date">{formatDate(o.created_at)}</p>
                  </div>
                  <div className="fan-account__order-right">
                    <p className="fan-account__order-total">Rp {Number(o.total_amount).toLocaleString('id-ID')}</p>
                    <span className={`fan-account__order-status fan-account__order-status--${o.payment_status}`}>
                      {STATUS_LABEL[o.payment_status] || o.payment_status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'following' && (
          <div>
            <div className="fan-account__following-grid">
              {data.following.length === 0 ? (
                <p className="fan-account__empty">Kamu belum follow artis manapun. Yuk follow artis favoritmu!</p>
              ) : (
                data.following.map((a) => (
                  <Link to={`/artists/${a.slug}`} className="fan-account__artist-chip" key={a.id}>
                    <div className="fan-account__artist-chip-photo">
                      {a.photo_url && <img src={a.photo_url} alt={a.name} />}
                    </div>
                    <span>{a.name}</span>
                  </Link>
                ))
              )}
            </div>

            <h3 className="fan-account__feed-title">Update Terbaru</h3>
            {feedLoading ? (
              <p className="fan-account__empty">Memuat update...</p>
            ) : feed.length === 0 ? (
              <p className="fan-account__empty">Belum ada update dari artis yang kamu follow.</p>
            ) : (
              <div className="fan-account__feed">
                {feed.map((item) => (
                  <div className="fan-account__feed-item" key={`${item.type}-${item.id}`}>
                    <div className="fan-account__feed-thumb">
                      {(item.artwork_url || item.thumbnail_url) && (
                        <img src={item.artwork_url || item.thumbnail_url} alt={item.title} />
                      )}
                    </div>
                    <div>
                      <p className="fan-account__feed-meta">
                        {item.artist_name} · {item.type === 'song' ? '🎵 Lagu baru' : '🎬 Video baru'}
                      </p>
                      <p className="fan-account__feed-title-text">{item.title}</p>
                      <p className="fan-account__feed-date">{formatDate(item.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
